import css from './questionItem.module.scss';

import cx from 'classnames';
import { memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMe } from 'com/App/WithMe';
import { useHistoryModal } from 'com/HistoryModal';
import { useQuestionEditor } from 'com/QuestionEditor';
import { apiMoveQuestionToTest } from 'core/api';
import { parseNote, prettifyText } from 'core/helpers/text';
import { getTestUrl } from 'core/paths';
import { ListPanel } from 'core/ui/ListPanel';
import { MenuItem } from 'core/ui/Menu';
import { useMessageModal } from 'core/ui/MessageModal';
import ICONS from 'core/ui/icons';

export type Props = {
  idx: number;
  question: TQuestion;
  test: TTest;
  onChangeQuestion: (updQuestion: TQuestion) => void;
  onRemoveQuestion: (questionUid: string) => Promise<void>;
  showOptions: boolean;
};

export function QuestionItem({
  question,
  onChangeQuestion,
  onRemoveQuestion,
  showOptions,
}: Props) {
  const { showError } = useMessageModal();
  const { editQuestion } = useQuestionEditor();

  const onEdit = async () => {
    try {
      const editedQuestion = await editQuestion(question.uid);
      if (editedQuestion) {
        onChangeQuestion(editedQuestion);
      }
    } catch (error) {
      showError(error);
    }
  };

  return (
    <ListPanel
      body={
        <div className={css.meta}>
          <div className={css.question}>{prettifyText(question.question)}</div>
          {question.hint && (
            <div className={css.hint}>({prettifyText(question.hint)})</div>
          )}
          {showOptions ? (
            <Options question={question} />
          ) : (
            <div className={css.rightOption}>
              <Text text={question.options[0]} />
            </div>
          )}
        </div>
      }
      imageResources={question.resourceUid ? [question.resourceUid] : []}
      exeStatuses={[
        {
          label: 'Question',
          lastProbeAt: question.lastProbeAt,
          nextProbeAt: question.nextProbeAt,
        },
      ]}
      actions={[
        [
          {
            title: 'Edit',
            onClick: onEdit,
            icon: ICONS.Edit,
          },
        ],
        [
          {
            icon: ICONS.Menu,
            title: 'Show menu',
            menuItems: (
              <MenuBody
                question={question}
                onRemoveQuestion={onRemoveQuestion}
              />
            ),
          },
        ],
      ]}
    />
  );
}

export default memo(QuestionItem);

function MenuBody({
  question,
  onRemoveQuestion,
}: {
  question: TQuestion;
  onRemoveQuestion: (questionUid: string) => Promise<void>;
}) {
  const me = useMe();
  const { showError, showMessage } = useMessageModal();
  const { showQuestionHistory } = useHistoryModal();

  const onRemove = async () => {
    try {
      if (!confirm('Are you sure?')) return;

      await onRemoveQuestion(question.uid);
    } catch (error) {
      showError(error);
    }
  };

  const navigate = useNavigate();

  const textToCopy = useCopyText(question);

  return (
    <>
      {me?.isAdmin && (
        <MenuItem
          label="Move to another test"
          icon={ICONS.ArrowRight}
          onClick={() => {
            // TODO: make a dialog.
            const testUid = prompt('Provide testUid');
            if (!testUid) return;
            apiMoveQuestionToTest(question.uid, testUid).catch(showError);
          }}
        />
      )}
      {me?.isAdmin && (
        <MenuItem
          label="Play in test"
          icon={ICONS.Play}
          onClick={() =>
            navigate(getTestUrl(question.testUid, 'all', question.uid))
          }
        />
      )}
      <MenuItem
        label="Copy"
        icon={ICONS.Cards}
        onClick={() => {
          navigator.clipboard
            .writeText(textToCopy)
            .then(() => showMessage('Copied!'))
            .catch(showError);
        }}
      />
      <MenuItem
        label="Show status"
        icon={ICONS.Graph}
        onClick={() => showQuestionHistory(question.uid)}
      />
      <MenuItem label="Remove" icon={ICONS.Trash} onClick={onRemove} />
    </>
  );
}

const useCopyText = (question: TQuestion): string => {
  return useMemo(
    () =>
      prettifyText(
        `${question.question}\n
    ${question.options
      .map((opt, idx) => `${String.fromCharCode(65 /* A */ + idx)}) ${opt}`)
      .join('\n\n')}
    `
          .replace(/^[ \t]+/gm, '')
          .trim()
      ),
    [question]
  );
};

function Text({ text }: { text: string }) {
  return parseNote(text).map((piece, idx) => (
    <span key={idx} className={cx(piece.type === 'secret' && css.secret)}>
      {piece.type === 'secret' && `❓ `}
      {prettifyText(piece.v)}
    </span>
  ));
}

function Options({ question }: { question: TQuestion }) {
  return (
    <div className={css.options}>
      <h3 className={css.optionsTitle}>Options:</h3>
      {question.options.map((opt, idx) => (
        <div key={idx} className={css.option}>
          <span className={css.point}>•</span>
          <Text text={opt} />
        </div>
      ))}
    </div>
  );
}
